// src/hooks/useDashboard.ts

import { useState, useEffect, useCallback } from 'react';
import type { DashboardData } from '../types/dashboard';
import dashboardService from '../services/dashboard.service';
import { useAuthContext } from '../contexts/AuthContext';

export const useDashboard = () => {
  const { 
    isAuthenticated, 
    isSuperAdmin, 
    isCommunalAdmin, 
    getAssignedCommuneId,
    user 
  } = useAuthContext();
  
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [data, setData] = useState<DashboardData | null>(null);

  const loadDashboardData = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      let response;
      
      // Load different dashboard data based on user role
      if (isSuperAdmin()) {
        // Super Admin: System-wide dashboard with communes overview
        const [systemResponse, communesResponse] = await Promise.all([
          dashboardService.getSystemDashboard(),
          dashboardService.getSystemCommunesOverview()
        ]);
        
        if (systemResponse.success && communesResponse.success) {
          // Combine system dashboard with communes overview
          response = {
            success: true,
            data: {
              ...systemResponse.data,
              communesOverview: communesResponse.data
            }
          };
        } else {
          throw new Error('Failed to load system dashboard or communes overview');
        }
      } else if (isCommunalAdmin()) {
        // Communal Admin: Only their assigned commune's data + income data
        const communeId = getAssignedCommuneId();
        if (!communeId) {
          throw new Error('Communal admin has no assigned commune');
        }
        
        // Communal Admin: Only their assigned commune's data (no additional API calls needed)
        response = await dashboardService.getCommuneDashboard(communeId);
      } else {
        // Fallback: generic dashboard (should not happen for group agents)
        response = await dashboardService.getGroupDashboard();
      }
      
      if (response.success) {
        // Extract the actual dashboard data from the nested response
        setData(response.data);
      } else {
        throw new Error(response.message || 'Failed to load dashboard data');
      }
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError(err instanceof Error ? err : new Error('Failed to load dashboard data'));
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user, isSuperAdmin, isCommunalAdmin, getAssignedCommuneId]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const refresh = useCallback(() => {
    return loadDashboardData();
  }, [loadDashboardData]);

  return {
    data,
    loading,
    error,
    refresh,
  };
};

export default useDashboard;